import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {Paper} from "@mui/material";
import Button from "../../../../../components/base/button/button";
import {Row} from "reactstrap";
import {Add, DeleteOutlined, EditOutlined, OpenInNew} from "@mui/icons-material";
import SearchInput from "../../../../../components/base/input/search_input";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import {DefaultPaginationInfo, HomepagePlatforms} from "../../../../../../core/constants/enums";
import IconButton from "../../../../../components/base/button/icon_button";
import {ReactComponent as WebIcon} from '../../../../../../assets/images/platform-icons/web.svg';
import {ReactComponent as PhoneIcon} from '../../../../../../assets/images/platform-icons/phone.svg';
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import {UserHomepageViewMode} from "../../../../../containers/user-homepage/models";
import AddNewHomepageEditionDialog from "../../../../../components/dialogs/add-new-homepage-edition";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import {booleanComparator} from "../../../../../../core/services/utils/utils";
import {ReactComponent as Passed} from "../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../assets/images/validations/failed.svg";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import {MainPageEditionDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {useHistory} from "react-router-dom";
import useQuery from "../../../../../hooks/use-query";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import DateUtils from "../../../../../../core/services/date-utils";

const editionTypes = {
    myEditions: 'myEditions',
    allEditions: 'allEditions',
}

const tabs = [
    {
        id: 1,
        label: "All Editions",
        value: editionTypes.allEditions,
    },
    {
        id: 2,
        label: "My Editions",
        value: editionTypes.myEditions,
    },
]

const defaultTab = editionTypes.allEditions;

const UserPlatformListView = () => {
    const history = useHistory()
    const [query] = useQuery()
    const [editions, setEditions] = useState([]);
    const [updatedTab, setUpdatedTab] = useState(query.tab ?? defaultTab);
    const [isAddingNewHomepageEdition, setIsAddingNewHomepageEdition] = useState(false);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('user-platform-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const clientPlatform = operatorAccessLevels.packages.clientPlatform;
    const canViewWebEditions = clientPlatform.webHomepageEditions;
    const canViewPhoneEditions = clientPlatform.phoneHomepageEditions;
    const canManageWebEditions = clientPlatform.manageWebHomepageEditions;
    const canManagePhoneEditions = clientPlatform.managePhoneHomepageEditions;
    const canAddEdition = canManageWebEditions || canManagePhoneEditions;
    const canExportEditions = (canViewWebEditions || canViewPhoneEditions)
        && clientPlatform.exportHomepageEditions;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.userPlatformList);
        configureBehavioralTool((tags) => tags.userPlatformList);
    }, [configureBehavioralTool, configureNotebookTags]);

    const [myEditionsDataGridSaveConfig, allEditionsDataGridSaveConfig] = useMemo(() => [
        ({
            name: 'UserPlatformListViewMyEditionsDataGrid',
            version: 1,
        }),
        ({
            name: 'UserPlatformListViewAllEditionsDataGrid',
            version: 1,
        })
    ], [])

    const columns = useMemo(() =>
        /** @return {DataGridColumn[]}*/
        [
            {
                name: MainPageEditionDataGridColumnNames.actions,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false
            },
            {
                title: 'Name',
                name: MainPageEditionDataGridColumnNames.name,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Platform',
                name: MainPageEditionDataGridColumnNames.platform,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 80,
            },
            {
                title: 'Submit Date & Time',
                name: MainPageEditionDataGridColumnNames.submitDateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                sortable: false,
                width: 150,
            },
            {
                title: 'Modified Date & Time',
                name: MainPageEditionDataGridColumnNames.modifiedDateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                sortable: false,
                width: 150,
            },
            {
                title: 'Submitted by',
                name: MainPageEditionDataGridColumnNames.submittedBy,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: 'Is live',
                name: MainPageEditionDataGridColumnNames.isLive,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
            },
        ], [])

    const [myEditionsDataGridColumns, , myEditionsDataGridPageSize, myEditionsDataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: myEditionsDataGridSaveConfig.name,
        version: myEditionsDataGridSaveConfig.version,
    });
    const [allEditionsDataGridColumns, , allEditionsDataGridPageSize, allEditionsDataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: allEditionsDataGridSaveConfig.name,
        version: allEditionsDataGridSaveConfig.version,
    });

    const [dataGridSaveConfig, dataGridColumns, dataGridPageSize, dataGridDensity] = useMemo(() => {
        switch (updatedTab) {
            case editionTypes.myEditions:
                return [myEditionsDataGridSaveConfig, myEditionsDataGridColumns, myEditionsDataGridPageSize, myEditionsDataGridDensity]
            case editionTypes.allEditions:
                return [allEditionsDataGridSaveConfig, allEditionsDataGridColumns, allEditionsDataGridPageSize, allEditionsDataGridDensity]
            default:
                return [];
        }
    }, [allEditionsDataGridColumns, allEditionsDataGridDensity, allEditionsDataGridPageSize, allEditionsDataGridSaveConfig, myEditionsDataGridColumns, myEditionsDataGridDensity, myEditionsDataGridPageSize, myEditionsDataGridSaveConfig, updatedTab])

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])


    /**
     * Searches for the main page editions in the system:
     * - Calls the api regarding this action,
     *  * if the api call was not successful, shows an error toast
     *  * otherwise, sets the [editions] in the state with that
     * @type {(function(UseSearchFunctionArg): Promise<undefined|ApiResponseDs<*>>)}
     */
    const search = useCallback(async (args) => {
        const forApi = {
            keyword: args.filters?.keyword?.toString(),
            showAllRequests: args.tab === editionTypes.allEditions,
            paginationDs: args.paginationInfo,
        };
        const response = await Api.searchHomepageEditions(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setEditions(response?.data?.items ?? [])
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0
            }))
        }
    }, [isMounted])

    const initialSearchDataSettings = useMemo(() => ({
        tab: updatedTab,
        paginationInfo: initialPaginationInfo,
    }), [updatedTab, initialPaginationInfo]);

    const searchData = useSearch(search, initialSearchDataSettings)


    /**
     * With each change in the [tab], updates the value of the [updatedTab] state.
     */
    useLayoutEffect(() => setUpdatedTab(searchData.tab), [searchData.tab])

    /**
     * Removes the homepage edition from the server.
     *
     * * if the response of the api is successful, removes the edition from the state list
     * @param edition
     * @return {Promise<void>}
     */
    const removeHomepageEdition = useCallback(async (edition) => {
        setEditions(prevState => prevState.map(e => e.id === edition.id ? {...e, removing: true} : e));
        const response = await Api.removeHomepageEdition(edition.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setEditions(prevState => prevState.filter(e => e.id !== edition.id));
        } else {
            setEditions(prevState => prevState.map(e => e.id === edition.id ? {...e, removing: false} : e));
        }
    }, [isMounted])

    /**
     * Handles adding a new home page:
     * - Opens a dialog for getting the title and platform from user
     * - Calls the api for creating a new home page
     * - redirects user to the created home page for adding components
     *
     * @return void
     */
    const onAddNewMainPageClicked = () => {
        setIsAddingNewHomepageEdition(true);
    }

    /**
     * Handles searching for the main page editions with the received keyword
     */
    const searchByKeyword = (keyword) => {
        keyword = keyword?.toString()?.trim();
        searchData.setFilters({
            keyword: keyword,
        })
    }

    /**
     * Handles changes in the tabs of the type of the editions
     */
    const onTabChanged = (e, newType) => {
        searchData.setTab(newType);
    }

    /**
     * Navigates the user to the single user platform edition view.
     *
     * * if [edit], then the starting mode of the view will be edit-mode otherwise, preview-mode
     * @param {any} edition
     * @param {boolean} edit
     */
    const navigateToMainPageEdition = useCallback((edition, edit = false) => {
        const type = edit ? UserHomepageViewMode.edit : UserHomepageViewMode.preview;
        switch (edition.platformId) {
            case HomepagePlatforms.phone:
                return history.push(routeFunctions.userPlatform.phoneHomepage(edition.id, type))
            case HomepagePlatforms.web:
                return history.push(routeFunctions.userPlatform.webHomepage(edition.id, type))
            default:
                break;
        }
    }, [history])

    /**
     * Gets users confirmation before removing the given edition entirely.
     */
    const getRemoveConfirmation = useCallback((edition) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to remove this user platform edition?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeHomepageEdition(edition),
        );
    }, [removeHomepageEdition])

    /**
     * Adds the newly created homepage edition to the list of homepage editions.
     * @param {any} edition
     */
    const addCreatedHomepageEdition = (edition) => {
        setEditions([
            ...editions.filter(e => e.isLive),
            edition,
            ...editions.filter(e => !e.isLive)
        ]);
        history.push(routeFunctions.userPlatform.webHomepage(edition.id, UserHomepageViewMode.edit));
    }

    /**
     * Exports visible homepage edition rows to CSV.
     */
    const exportEditionsToCsv = useCallback(async () => ({
        title: `Homepage Editions - ${DateUtils.localNow().format(DateUtils.dateTimeFormat)}`,
        body: editions.map((edition) => ({
            Name: edition?.name ?? '',
            Platform: edition.platformId === HomepagePlatforms.web ? 'Web' : 'Phone',
            'Submit Date & Time': edition?.submittedDateTime
                ? DateUtils.serverToLocal(edition.submittedDateTime).format(DateUtils.dateTimeFormat)
                : '',
            'Modified Date & Time': edition?.modifiedDateTime
                ? DateUtils.serverToLocal(edition.modifiedDateTime).format(DateUtils.dateTimeFormat)
                : '',
            'Submitted by': edition?.operatorName ?? '',
            'Is live': edition.isLive ? 'Yes' : 'No',
        })),
    }), [editions]);

    /**
     * Creates table entities elements
     * @type {DataGridRow[]}
     */
    const dataGridRows = useMemo(() => {
        return editions
            ?.sort((a, b) => booleanComparator(b.isLive, a.isLive))
            ?.map((edition) => {
                const isWeb = edition.platformId === HomepagePlatforms.web;
                const canView = isWeb ? canViewWebEditions : canViewPhoneEditions;
                const canManage = isWeb ? canManageWebEditions : canManagePhoneEditions;
                return {
                    key: edition?.id,
                    data: edition,
                    className: edition.isLive ? "special-table-row" : "",
                    cells: {
                        [MainPageEditionDataGridColumnNames.actions]:
                            <div className={'d-flex align-items-center justify-content-start w-100'}>
                                {
                                    canView &&
                                    <IconButton
                                        color={'secondary'}
                                        className={'mr-2'}
                                        onPressed={() => navigateToMainPageEdition(edition)}>
                                        <OpenInNew className='icon normal'/>
                                    </IconButton>
                                }
                                {
                                    canManage &&
                                    <IconButton
                                        color={'secondary'}
                                        className={'mr-2'}
                                        onPressed={() => navigateToMainPageEdition(edition, true)}>
                                        <EditOutlined className='icon normal'/>
                                    </IconButton>
                                }
                                {
                                    !edition.isLive && canManage &&
                                    <IconButton
                                        color={'paper.error'}
                                        disabled={!(!edition.isLive && canManage)}
                                        onClick={() => getRemoveConfirmation(edition)}
                                    >
                                        <DeleteOutlined className='icon normal'/>
                                    </IconButton>
                                }
                            </div>,
                        [MainPageEditionDataGridColumnNames.name]: edition?.name,
                        [MainPageEditionDataGridColumnNames.platform]: () => edition.platformId === HomepagePlatforms.web
                            ? <WebIcon className={'platform-icon'}/>
                            : <PhoneIcon className={'platform-icon'}/>,
                        [MainPageEditionDataGridColumnNames.submitDateTime]: edition?.submittedDateTime,
                        [MainPageEditionDataGridColumnNames.modifiedDateTime]: edition?.modifiedDateTime,
                        [MainPageEditionDataGridColumnNames.submittedBy]: edition?.operatorName,
                        [MainPageEditionDataGridColumnNames.isLive]: () => edition.isLive
                            ? <Passed/>
                            : <Failed/>,
                    },
                }
            })
    }, [canManagePhoneEditions, canManageWebEditions, canViewPhoneEditions, canViewWebEditions, editions, getRemoveConfirmation, navigateToMainPageEdition])

    return (
        <>
            <Row className='no-gutters w-100 align-items-start justify-content-center main-page-editions-view'>
                <div className={'d-flex flex-row justify-content-between align-items-center w-100 mt-4 mb-4 px-3'}>
                    <p className={'view-title'}>
                        Homepage Editions
                    </p>
                    {
                        canAddEdition &&
                        <Button
                            className='justify-content-center align-items-center d-flex'
                            variant={'outlined'}
                            color={'primary'}
                            onClick={onAddNewMainPageClicked}
                        >
                            <Add className='icon small'/>
                            <p className={'button-label'}>
                                Add a new Edition
                            </p>
                        </Button>
                    }
                </div>
                <div className={'search-wrapper'}>
                    <SearchInput
                        autoSearch
                        immediateSearch
                        loading={searchData.loading}
                        initialValue={searchData.filters?.keyword ?? ''}
                        search={searchByKeyword}
                        label={'Keyword'}
                    />
                </div>
                <div className='w-100 flex-grow-1'>
                    <Tabs value={searchData.tab ?? false} onChange={onTabChanged}>
                        {
                            tabs.map(tab => (
                                <Tab key={tab.id} value={tab.value} label={tab.label}/>
                            ))
                        }
                    </Tabs>
                    <div className='my-4 w-100'/>
                </div>
                <Paper className='paper w-100 px-3 pt-2 flex-wrap justify-content-between align-items-start'>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        classNames={{
                            container: 'homepage-platforms-list-table',
                        }}
                        rows={dataGridRows}
                        columns={dataGridColumns}
                        getExportInfo={canExportEditions ? exportEditionsToCsv : undefined}
                        hideFooterActions
                        state={{
                            loading: {state: searchData.loading},
                            pagination: searchData.paginationInfo,
                            density: dataGridDensity,
                        }}
                        onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                        onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    />
                </Paper>
            </Row>
            <AddNewHomepageEditionDialog
                open={isAddingNewHomepageEdition}
                close={() => setIsAddingNewHomepageEdition(false)}
                onSuccess={addCreatedHomepageEdition}
            />
        </>
    )
}

export default UserPlatformListView;
